import { RouteCodeEnum } from '@models/menu';

export enum QueueJobType {
  Print = 1,
  Download = 2,
  Email = 3,
}

export enum QueueJobStatus {
  Queued = 1,
  Processing = 2,
  Completed = 3,
  Failed = 4,
}

export interface QueueOperationResponse {
  operationId: string;
  jobId?: string;
}

/**
 * Base contract that every server-side queue notification payload implements.
 */
export type QueueNotificationPayload = object;

/**
 * Notification payload for queue operations that generate a temporary file (e.g., Print, Download, Email).
 */
export interface QueueFileNotificationPayload extends QueueNotificationPayload {
  fileName?: string;
  contentType?: string;
}

/**
 * Generic SignalR notification model for background queue operations.
 */
export interface QueueNotificationModel<TPayload extends QueueNotificationPayload = QueueNotificationPayload> {
  operationId: string;
  jobType: QueueJobType;
  status: QueueJobStatus;
  payload?: TPayload;
  errorMessage?: string;
}

/**
 * Base contract that every client-side pending queue operation payload implements.
 */
export type QueuePendingPayload = object;

/**
 * Pending operation payload for file-producing queue operations (Print, Download).
 */
export interface QueueFilePendingPayload extends QueuePendingPayload {
  fileName: string;
}

/**
 * Pending operation payload for Email queue operations (extends file payload with email dialog context).
 */
export interface QueueEmailPendingPayload extends QueueFilePendingPayload {
  contractId: number;
  contractNo: string;
  moduleId: number;
  moduleName: string;
  moduleCode: RouteCodeEnum;
  entityInstanceIds: number[];
}

/**
 * Generic client-side descriptor for a tracked background queue operation.
 */
export interface QueuePendingOperation<TPayload extends QueuePendingPayload = QueuePendingPayload> {
  operationId: string;
  jobId?: string;
  jobType: QueueJobType;
  payload: TPayload;
  onSettled?: (status: QueueJobStatus.Completed | QueueJobStatus.Failed) => void;
  onFileReady?: (blob: Blob, fileName: string, contentType: string) => void;
}
